import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import "./no-scroll.css";
import SectionHeader from "../SectionHeader";
import { Link } from "react-router-dom";
import { FaUserNurse, FaUserTie, FaCheck, FaArrowLeft, FaStar, FaQuoteLeft, FaQuoteRight } from "react-icons/fa";
import { GiRunningShoe, GiDirectionSign } from "react-icons/gi";
import { MdFamilyRestroom, MdOutlineSelfImprovement, MdOutlineFemale, MdOutlineFavorite } from "react-icons/md";
import { useTranslation } from "react-i18next";

// Import translations
import fa from "./locales/HomeUserPersonas-fa.json";
import en from "./locales/HomeUserPersonas-en.json";
import ar from "./locales/HomeUserPersonas-ar.json";
import tr from "./locales/HomeUserPersonas-tr.json";

interface TestimonialItem {
    text: string;
    author: string;
    role: string;
}

interface Persona {
    id: string;
    icon: React.ReactNode;
    title: string;
    headline: string;
    description: string;
    features: string[];
    testimonials: TestimonialItem[];
    cta: {
        text: string;
        color: string;
    };
    bgColor: string;
    comingSoon?: boolean;
}

const personaTestimonialsMap: Record<string, Record<string, TestimonialItem[]>> = {
    "wellness": {
        fa: [
            { text: "با ابزارهای خودمراقبتی دلبند، سطح انرژی و کیفیت خواب روزانه‌ام به شکل چشمگیری بهبود یافته است.", author: "سارا محمدی", role: "علاقه‌مند به خودمراقبتی" },
            { text: "پایش مداوم ضربان قلب و استرس به من کمک کرد ریتم کاری روزمره‌ام را بهتر تنظیم کنم.", author: "آرش کمالی", role: "برنامه‌نویس و فریلنسر" },
            { text: "تمرینات هوشمند تنفسی قبل از خواب، بهترین بخش روتین شبانه من شده است.", author: "مهسا ناصری", role: "طراح محصول" },
        ],
        en: [
            { text: "With Delband self-care tools, my daily energy and sleep quality improved noticeably.", author: "Sara Mohammadi", role: "Self-Care Enthusiast" },
            { text: "Continuous heart rate and stress monitoring helped me regulate my work pace.", author: "Arash Kamali", role: "Software Engineer" },
            { text: "Guided breathwork before sleep has become the highlight of my nightly routine.", author: "Mahsa Naseri", role: "Product Designer" },
        ],
        ar: [
            { text: "مع أدوات الرعاية الذاتية من دلبند، تحسنت طاقتي وجودة نومي بشكل ملحوظ.", author: "سارة محمدي", role: "مهتمة بالرعاية الذاتية" },
            { text: "ساعدني تتبع نبضات القلب والتوتر على تنظيم إيقاع عملي اليومي.", author: "آرش كمالي", role: "مطور برمجيات" },
            { text: "تمارين التنفس الذكية قبل النوم أصبحت أفضل جزء في روتيني المسائي.", author: "مهسا ناصري", role: "مصممة منتجات" },
        ],
        tr: [
            { text: "Delband öz bakım araçlarıyla günlük enerjim ve uyku kalitem gözle görülür şekilde arttı.", author: "Sara Mohammadi", role: "Öz Bakım Meraklısı" },
            { text: "Sürekli nabız ve stres takibi çalışma tempomu dengelememe yardımcı oldu.", author: "Arash Kamali", role: "Yazılım Geliştirici" },
            { text: "Uyku öncesi akıllı nefes egzersizleri gece rutinimin en iyi parçası oldu.", author: "Mahsa Naseri", role: "Ürün Tasarımcısı" },
        ],
    },
    "women-health": {
        fa: [
            { text: "دلبند به من کمک کرد چرخه‌ام را دقیق‌تر بشناسم و روزهای پرانرژی‌ام را برنامه‌ریزی کنم.", author: "نیلوفر رضایی", role: "تحلیلگر نرم‌افزار و مادر" },
            { text: "پایش دمای بدن و پیش‌بینی هوشمند چرخه، آرامش خیال فوق‌العاده‌ای به من داده.", author: "زهرا طاهری", role: "داروساز" },
            { text: "طراحی سبک و راحتی رینگ روی انگشت در تمام طول شبانه‌روز حس آرامش دارد.", author: "رویا افشار", role: "مشاور سلامت" },
        ],
        en: [
            { text: "Delband helped me understand my cycle and schedule my highest-energy days.", author: "Niloufar Rezaei", role: "Software Analyst & Mother" },
            { text: "Body temperature tracking and smart cycle predictions brought immense peace of mind.", author: "Zahra Taheri", role: "Pharmacist" },
            { text: "The lightweight ring design feels effortless and comfortable all day and night.", author: "Roya Afshar", role: "Health Consultant" },
        ],
        ar: [
            { text: "ساعدني دلبند في فهم دورتي وتنظيم أيامي الأكثر إنتاجية وطاقة.", author: "نيلوفر رضائي", role: "محللة نظم وأم" },
            { text: "تتبع درجة حرارة الجسم والتنبؤ الذكي بالدورة منحني راحة بال حقيقية.", author: "زهراء طاهري", role: "صيدلانية" },
            { text: "تصميم الخاتم الخفيف ومريح للغاية على الإصبع طوال ساعات اليوم.", author: "رؤيا أفشار", role: "مستشارة صحية" },
        ],
        tr: [
            { text: "Delband döngümü daha iyi anlamama ve enerjik günlerimi planlamama yardımcı oldu.", author: "Niloufar Rezaei", role: "Yazılım Analisti ve Anne" },
            { text: "Vücut ısısı takibi ve akıllı döngü tahmini harika bir iç huzuru sağladı.", author: "Zahra Taheri", role: "Eczacı" },
            { text: "Hafif ve şık yüzük tasarımı gün boyu parmakta büyük bir konfor sunuyor.", author: "Roya Afshar", role: "Sağlık Danışmanı" },
        ],
    },
    "athletes": {
        fa: [
            { text: "داده‌های دقیق دلبند به من کمک کرد تمرین‌ها را هدفمند و ریکاوری را مدیریت کنم.", author: "علی محمدی", role: "ورزشکار سه‌گانه" },
            { text: "معیارهای HRV و پنجره ریکاوری بعد از هر تمرین سنگین واقعاً کاربردی است.", author: "سینا پرویزی", role: "دونده ماراتن" },
            { text: "پایش اکسیژن خون و تحلیل نوسانات ضربان، عملکرد ورزشم را ارتقا داده است.", author: "کامران صبوری", role: "مربی تناسب اندام" },
        ],
        en: [
            { text: "Delband's accurate data helped me target workouts and optimize recovery.", author: "Ali Mohammadi", role: "Triathlete" },
            { text: "HRV metrics and recovery windows after intense sessions are extremely practical.", author: "Sina Parvizi", role: "Marathon Runner" },
            { text: "Blood oxygen tracking and heart rate trend analysis elevated my performance.", author: "Kamran Sabouri", role: "Fitness Coach" },
        ],
        ar: [
            { text: "بيانات دلبند الدقيقة ساعدتني في توجيه التدريبات وإدارة الاستشفاء بذكاء.", author: "علي محمدي", role: "رياضي ثلاثي" },
            { text: "مقاييس تقلب معدل ضربات القلب (HRV) وفترات الاستشفاء مفيدة وعملية للغاية.", author: "سينا برويزي", role: "عداء ماراثون" },
            { text: "مراقبة الأكسجين في الدم وتحليل النبض رفعت من مستوى أدائي البدني.", author: "كامران صبوري", role: "مدرب لياقة بدنية" },
        ],
        tr: [
            { text: "Delband'ın hassas verileri antrenmanları hedeflememe ve toparlanmayı yönetmeme yardımcı oldu.", author: "Ali Mohammadi", role: "Triatlon Sporcusu" },
            { text: "HRV metrikleri ve toparlanma penceresi ağır antrenmanlardan sonra çok kullanışlı.", author: "Sina Parvizi", role: "Maraton Koşucusu" },
            { text: "Kandaki oksijen ve nabız dalgalanması analizi spor performansımı yükseltti.", author: "Kamran Sabouri", role: "Fitness Antrenörü" },
        ],
    },
    "child-monitoring": {
        fa: [
            { text: "با دلبند دیگر نگران مسیر مدرسه و آرامش کودک‌ام نیستم — آرامش واقعی است.", author: "مریم حسینی", role: "مادر کودک ۷ ساله" },
            { text: "قابلیت محدوده امن و هشدار خروج باعث شده در رفت‌وآمد خیالم کاملاً راحت باشد.", author: "امیرحسین رضایی", role: "پدر دو فرزند" },
            { text: "دکمه تماس اضطراری SOS، امنیت فرزندم را در هر شرایطی تضمین می‌کند.", author: "فاطمه رهنما", role: "معلم و مادر" },
        ],
        en: [
            { text: "With Delband, I no longer worry about my child's school route — true peace of mind.", author: "Maryam Hosseini", role: "Mother of a 7-Year-Old" },
            { text: "Safe geo-fencing and exit alerts give total confidence during daily commutes.", author: "Amir Hossein Rezaei", role: "Father of Two" },
            { text: "The dedicated SOS emergency button guarantees my child's security everywhere.", author: "Fatemeh Rahnama", role: "Teacher & Mother" },
        ],
        ar: [
            { text: "مع دلبند لم أعد قلقة بشأن طريق طفلي إلى المدرسة — راحة بال حقيقية.", author: "مريم حسيني", role: "أم لطفل بعمر 7 سنوات" },
            { text: "خاصية المنطقة الآمنة وتنبيهات الخروج تمنح طمأنينة تامة في التنقلات.", author: "أمير حسين رضائي", role: "أب لطفلين" },
            { text: "زر الطوارئ SOS السريع يضمن سلامة وأمان طفلي في جميع الأوقات.", author: "فاطمة رهنما", role: "معلمة وأم" },
        ],
        tr: [
            { text: "Delband ile çocuğumun okul yolunu dert etmiyorum — gerçek bir huzur.", author: "Maryam Hosseini", role: "7 Yaşında Çocuk Annesi" },
            { text: "Güvenli bölge (Geo-fence) ve çıkış uyarıları günlük gidiş-gelişlerde tam güven veriyor.", author: "Amir Hossein Rezaei", role: "İki Çocuk Babası" },
            { text: "Acil durum SOS düğmesi çocuğumun güvenliğini her an garanti altına alıyor.", author: "Fatemeh Rahnama", role: "Öğretmen ve Anne" },
        ],
    },
    "caregivers": {
        fa: [
            { text: "واکنش سریع دلبند بعد از سقوط پدرم، باعث شد دل‌گرم‌تر و آسوده‌خاطر باشم.", author: "نرگس صادقی", role: "مراقب خانواده" },
            { text: "یادآوری منظم مصرف دارو و گزارش شاخص‌های حیاتی مادرم، فوق‌العاده دقیق است.", author: "مجید سلطانی", role: "فرزند مراقب" },
            { text: "امکان دیدن وضعیت ضربان قلب و استراحت والدین از راه دور یک نعمت بزرگ است.", author: "الهام قدیری", role: "مهندس و مراقب" },
        ],
        en: [
            { text: "Delband's instant alert after my father's fall gave our family tremendous reassurance.", author: "Narges Sadeghi", role: "Family Caregiver" },
            { text: "Regular medication reminders and vital sign trends for my mother are incredibly precise.", author: "Majid Soltani", role: "Caring Son" },
            { text: "Remotely checking my parents' heart rate and resting state is an invaluable blessing.", author: "Elham Ghadiri", role: "Engineer & Caregiver" },
        ],
        ar: [
            { text: "الاستجابة الفورية من دلبند عند سقوط والدي منحت عائلتنا أماناً كبيراً.", author: "نرجس صادقي", role: "مقدمة رعاية عائلية" },
            { text: "التذكير المنتظم بمواعيد الدواء ومؤشرات والدتي الحيوية دقيقة للغاية.", author: "مجيد سلطاني", role: "ابن معتني" },
            { text: "إمكانية الاطمئنان على نبض وراحة الوالدين عن بعد نعمة حقيقية.", author: "إلهام قديري", role: "مهندسة ومقدمة رعاية" },
        ],
        tr: [
            { text: "Babamın düşmesinden sonra Delband'ın anında bildirim göndermesi bize büyük güven verdi.", author: "Narges Sadeghi", role: "Aile Bakıcısı" },
            { text: "Annemin düzenli ilaç hatırlatması ve yaşamsal belirti takibi son derece kusursuz.", author: "Majid Soltani", role: "Bakım Veren Evlat" },
            { text: "Ebeveynlerimin nabız ve dinlenme durumunu uzaktan görebilmek paha biçilmez.", author: "Elham Ghadiri", role: "Mühendis ve Bakıcı" },
        ],
    },
    "hr-managers": {
        fa: [
            { text: "پس از پیاده‌سازی دلبند، واکنش تیم HSE و شفافیت گزارش‌های ایمنی بسیار بهتر شد.", author: "پرهام صادقی", role: "مدیر HSE" },
            { text: "پایش خستگی و سلامت شیفت‌های شبانه، ریسک حوادث محیط کار را به حداقل رساند.", author: "کیانوش ابراهیمی", role: "مدیر منابع انسانی" },
            { text: "داشبورد سازمانی جامع دلبند تصمیم‌گیری‌های ارتقای سلامت پرسنل را داده‌محور کرد.", author: "ندا افخمی", role: "سرپرست بهداشت حرفه‌ای" },
        ],
        en: [
            { text: "Deploying Delband significantly improved our HSE team's agility and reporting transparency.", author: "Parham Sadeghi", role: "HSE Director" },
            { text: "Monitoring fatigue and night-shift wellness minimized occupational hazard risks.", author: "Kianoush Ebrahimi", role: "HR Manager" },
            { text: "Delband's comprehensive enterprise dashboard made employee wellness data-driven.", author: "Neda Afkhami", role: "Occupational Health Lead" },
        ],
        ar: [
            { text: "بعد تطبيق دلبند، تحسنت سرعة استجابة فريق السلامة وشفافية التقارير بشكل ممتاز.", author: "برهام صادقي", role: "مدير السلامة والصحة" },
            { text: "متابعة الإجهاد وصحة المناوبات الليلية خفضت مخاطر الحوادث التشغيلية للحد الأدنى.", author: "كيانوش إبراهيمي", role: "مدير الموارد البشرية" },
            { text: "لوحة التحكم المؤسسية لدلبند جعلت قرارات تعزيز صحة الموظفين مبنية على البيانات.", author: "ندا أفخمي", role: "مشرفة الصحة المهنية" },
        ],
        tr: [
            { text: "Delband'ın kurulumundan sonra İSG ekibimizin müdahale hızı ve rapor şeffaflığı büyük oranda arttı.", author: "Parham Sadeghi", role: "İSG Direktörü" },
            { text: "Gece vardiyalarında yorgunluk ve sağlık takibi iş kazası risklerini minimuma indirdi.", author: "Kianoush Ebrahimi", role: "İnsan Kaynakları Müdürü" },
            { text: "Kapsamlı kurumsal gösterge paneli personel sağlığı kararlarını veri odaklı hale getirdi.", author: "Neda Afkhami", role: "İş Sağlığı Uzmanı" },
        ],
    },
    "couples": {
        fa: [
            { text: "با اشتراک‌گذاری ایمنِ دلبند، سفرهایمان بدون استرس شد و حس نزدیکی بهتری داریم.", author: "رضا و نازنین موسوی", role: "زوج شاغل" },
            { text: "دیدن سطح انرژی و علائم همدیگر در طول روز، همدلی و توجه متقابل را دوچندان کرده.", author: "پویا و ساناز یوسفی", role: "زوج ماجراجو" },
            { text: "اعلان رسیدن امن به مقصد در مسیرهای روزمره، استرس فاصله را کاملاً از بین برده است.", author: "فرهاد و مونا خانی", role: "ساکنان دو شهر" },
        ],
        en: [
            { text: "With Delband's secure sharing, our travels are stress-free and we feel closer than ever.", author: "Reza & Nazanin Mousavi", role: "Working Couple" },
            { text: "Seeing each other's daily wellness and energy levels reinforces mutual empathy and care.", author: "Pouya & Sanaz Yousefi", role: "Active Couple" },
            { text: "Safe arrival notifications on daily commutes have completely removed distance worries.", author: "Farhad & Mona Khani", role: "Intercity Couple" },
        ],
        ar: [
            { text: "مع المشاركة الآمنة من دلبند، أصبحت رحلاتنا خالية من التوتر ونشعر بقرب أكبر.", author: "رضا ونازنين موسوي", role: "زوجان عاملان" },
            { text: "الاطلاع على مستوى الطاقة والصحة لبعضنا يومياً يضاعف الاهتمام والرعاية المتبادلة.", author: "بويا وساناز يوسفي", role: "زوجان مغامران" },
            { text: "إشعارات الوصول الآمن في التنقلات اليومية أزالت تماماً القلق من المسافات.", author: "فرهاد ومنى خاني", role: "زوجان في مدينتين" },
        ],
        tr: [
            { text: "Delband'ın güvenli paylaşımı sayesinde seyahatlerimiz stressiz geçiyor ve daha yakınız.", author: "Reza ve Nazanin Mousavi", role: "Çalışan Çift" },
            { text: "Gün içinde birbirimizin enerji ve sağlık durumunu görmek karşılıklı ilgiyi artırıyor.", author: "Pouya ve Sanaz Yousefi", role: "Macera Sever Çift" },
            { text: "Günlük yollarda hedefe güvenli varış bildirimleri mesafe endişelerini tamamen yok etti.", author: "Farhad ve Mona Khani", role: "Farklı Şehirlerde Yaşayan Çift" },
        ],
    },
};

const HomeUserPersonas = () => {
    const { t, i18n } = useTranslation();

    // Register component translations
    useEffect(() => {
        const ns = "HomeUserPersonas";
        const resources = { fa, en, ar, tr } as const;

        Object.entries(resources).forEach(([lng, data]) => {
            if (!i18n.hasResourceBundle(lng, ns)) {
                i18n.addResourceBundle(lng, ns, data, true, true);
            }
        });
    }, [i18n]);

    const [activeTab, setActiveTab] = useState(0);
    const [currentCardIndex, setCurrentCardIndex] = useState(0);
    const [isHovered, setIsHovered] = useState(false);
    const [isTyping, setIsTyping] = useState(false);

    // Determine text direction based on current language
    const currentLang = i18n.language;
    const isRTL = ["fa", "ar"].includes(currentLang);
    const textAlign = isRTL ? "text-right" : "text-left";
    const dir = isRTL ? "rtl" : "ltr";
    const flexDir = isRTL ? "flex-row-reverse" : "flex-row";

    const personaConfigMap: Record<string, {
        icon: React.ReactNode;
        color: string;
        bgColor: string;
        comingSoon: boolean;
        image: string;
    }> = {
        "wellness": {
            icon: <MdOutlineSelfImprovement className="text-3xl" />,
            color: "bg-green-500 hover:bg-green-600",
            bgColor: "bg-green-50",
            comingSoon: false,
            image: "e7bc04b4-803c-4854-8c49-3bb21a07e0f5.webp",
        },
        "women-health": {
            icon: <MdOutlineFemale className="text-3xl" />,
            color: "bg-rose-500 hover:bg-rose-600",
            bgColor: "bg-rose-50",
            comingSoon: false,
            image: "women-health-testimonial.webp",
        },
        "athletes": {
            icon: <GiRunningShoe className="text-3xl" />,
            color: "bg-emerald-500 hover:bg-emerald-600",
            bgColor: "bg-emerald-50",
            comingSoon: false,
            image: "3e976611-6477-4530-a4cf-dde4c813c036.webp",
        },
        "child-monitoring": {
            icon: <MdFamilyRestroom className="text-3xl" />,
            color: "bg-blue-600 hover:bg-blue-700",
            bgColor: "bg-blue-50",
            comingSoon: false,
            image: "b1d0e6be-8b44-40b3-b8d2-9652d9f7421f.webp",
        },
        "caregivers": {
            icon: <FaUserNurse className="text-2xl" />,
            color: "bg-purple-500 hover:bg-purple-600",
            bgColor: "bg-purple-50",
            comingSoon: false,
            image: "e2411267-b584-48c8-b70e-a392630ff9b4.webp",
        },
        "hr-managers": {
            icon: <FaUserTie className="text-2xl" />,
            color: "bg-blue-500 hover:bg-blue-600",
            bgColor: "bg-blue-50",
            comingSoon: true,
            image: "b767861d-399d-4899-ac87-106c01184e75.webp",
        },
        "couples": {
            icon: <MdOutlineFavorite className="text-2xl" />,
            color: "bg-pink-500 hover:bg-pink-600",
            bgColor: "bg-pink-50",
            comingSoon: true,
            image: "ece7bd48-27a1-49e1-b2bc-4c58681d959d.webp",
        },
    };

    const personas: Persona[] = Array.from({ length: 7 }, (_, index) => {
        const id = t(`personas.${index}.id`, { ns: "HomeUserPersonas" });
        const config = personaConfigMap[id] || {
            icon: <MdFamilyRestroom className="text-3xl" />,
            color: "bg-blue-600 hover:bg-blue-700",
            bgColor: "bg-blue-50",
            comingSoon: false,
            image: "b1d0e6be-8b44-40b3-b8d2-9652d9f7421f.webp",
        };

        // Get features as array properly
        const featuresData = [];
        for (let i = 0; i < 10; i++) {
            const feature = t(`personas.${index}.features.${i}`, { ns: "HomeUserPersonas", defaultValue: "" });
            if (feature && feature !== `personas.${index}.features.${i}`) {
                featuresData.push(feature);
            } else {
                break;
            }
        }

        const localizedTestimonials = (personaTestimonialsMap[id] && (personaTestimonialsMap[id][currentLang] || personaTestimonialsMap[id].fa)) || [
            {
                text: t(`personas.${index}.testimonial.text`, { ns: "HomeUserPersonas" }),
                author: t(`personas.${index}.testimonial.author`, { ns: "HomeUserPersonas" }),
                role: t(`personas.${index}.testimonial.role`, { ns: "HomeUserPersonas" }),
            }
        ];

        return {
            id,
            icon: config.icon,
            title: t(`personas.${index}.title`, { ns: "HomeUserPersonas" }),
            headline: t(`personas.${index}.headline`, { ns: "HomeUserPersonas" }),
            description: t(`personas.${index}.description`, { ns: "HomeUserPersonas" }),
            features: featuresData,
            testimonials: localizedTestimonials,
            cta: {
                text: t(`personas.${index}.cta.text`, { ns: "HomeUserPersonas" }),
                color: config.color,
            },
            bgColor: config.bgColor,
            comingSoon: config.comingSoon,
        };
    });

    // Reset card index when activeTab changes
    useEffect(() => {
        setCurrentCardIndex(0);
    }, [activeTab]);

    // Auto rotate testimonial cards every 4.5 seconds
    useEffect(() => {
        if (isHovered) return;
        const currentList = personas[activeTab]?.testimonials || [];
        if (currentList.length <= 1) return;

        const timer = setInterval(() => {
            setCurrentCardIndex((prev) => (prev + 1) % currentList.length);
        }, 4500);

        return () => clearInterval(timer);
    }, [activeTab, isHovered, personas]);

    useEffect(() => {
        setIsTyping(true);
        const timer = setTimeout(() => setIsTyping(false), 1500);
        return () => clearTimeout(timer);
    }, [activeTab]);

    return (
        <section dir={dir} className="relative overflow-hidden">
            <div className="container  mx-auto">
                <div className="text-center mb-2">
                    <SectionHeader regularText={t("header.regularText", { ns: "HomeUserPersonas" })} highlightedText={t("header.highlightedText", { ns: "HomeUserPersonas" })} />
                    <motion.p
                        className={`text-gray-600 text-lg max-w-2xl mx-auto mt-[-8px] ${textAlign}`}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.2 }}
                    >
                        {t("header.description", { ns: "HomeUserPersonas" })}
                    </motion.p>
                </div>

                <div className="relative w-full md:w-10/12 mx-auto overflow-x-auto pt-4 sm:pt-8 pb-8 sm:pb-14 no-scrollbar snap-x snap-mandatory px-3">
                    <div className="flex gap-3.5 sm:gap-5 md:gap-6 min-w-max px-2 sm:px-4 items-center">
                        {personas.map((persona, index) => (
                            <motion.button
                                key={persona.id}
                                onClick={() => setActiveTab(index)}
                                className={`flex items-center gap-2 sm:gap-2.5 px-4 sm:px-6 py-2.5 sm:py-3 rounded-full transition-all duration-300 relative text-xs sm:text-sm md:text-base snap-center shrink-0 whitespace-nowrap border
                ${activeTab === index ? "bg-[#0072CE] border-[#0072CE] text-white shadow-md shadow-blue-500/20 scale-[1.02] sm:scale-105" : "bg-white border-slate-200/80 text-gray-700 hover:bg-gray-50 hover:border-gray-300 hover:scale-[1.02]"}`}
                                whileHover={{ scale: activeTab === index ? 1.05 : 1.03 }}
                                whileTap={{ scale: 0.96 }}
                                type="button"
                            >
                                <span className="text-lg sm:text-xl shrink-0">{persona.icon}</span>
                                <span className="font-semibold">{persona.title}</span>

                                {persona.comingSoon && (
                                    <div className={`absolute -top-1 ${isRTL ? "-left-2" : "-right-2"} transform ${isRTL ? "-rotate-12" : "rotate-12"} z-20`}>
                                        <div className="bg-blue-100 text-blue-700 text-[8px] px-2 py-0.5 rounded-full font-medium shadow-xs border border-blue-200">
                                            {t("comingSoonBadge", { ns: "HomeUserPersonas" })}
                                        </div>
                                    </div>
                                )}
                            </motion.button>
                        ))}
                    </div>
                </div>

                <AnimatePresence mode="popLayout">
                    <motion.div
                        key={activeTab}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        className={`rounded-2xl sm:rounded-3xl p-4 sm:p-6 md:p-8 ${personas[activeTab].bgColor} relative overflow-hidden`}
                    >
                        <motion.div
                            className={`absolute top-0 ${isRTL ? "right-0" : "left-0"} w-full h-full opacity-10`}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 0.1 }}
                            transition={{ duration: 1 }}
                            style={{
                                backgroundImage: `radial-gradient(circle at ${isRTL ? "80%" : "20%"} 30%, rgba(255,255,255,0.8) 0%, transparent 50%), 
                                              radial-gradient(circle at ${isRTL ? "20%" : "80%"} 70%, rgba(255,255,255,0.8) 0%, transparent 50%)`,
                            }}
                        />

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-14 relative z-10">
                            {/* Priority 1 on Mobile & Desktop: Main Content (Headline, Description, Features list, CTA) */}
                            <div className="space-y-4 sm:space-y-6 order-1">
                                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="relative">
                                    <h3 className={`text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 relative inline-block ${textAlign}`}>
                                        {isTyping ? (
                                            <motion.span initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.1, staggerChildren: 0.1 }}>
                                                {personas[activeTab].headline.split("").map((char, index) => (
                                                    <motion.span key={index} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.03 }}>
                                                        {char}
                                                    </motion.span>
                                                ))}
                                            </motion.span>
                                        ) : (
                                            <motion.span
                                                initial={{ opacity: 1 }}
                                                whileHover={{
                                                    color: "#0072CE",
                                                    transition: { duration: 0.2 },
                                                }}
                                            >
                                                {personas[activeTab].headline}
                                            </motion.span>
                                        )}
                                    </h3>
                                </motion.div>

                                <motion.p
                                    className={`text-gray-600 text-sm sm:text-base leading-relaxed ${textAlign} relative`}
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.4, delay: 0.2 }}
                                >
                                    {personas[activeTab].description}
                                </motion.p>

                                <motion.ul role="list" className="space-y-2.5 sm:space-y-4 relative list-none p-0 m-0" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.4, delay: 0.3 }}>
                                    <motion.div
                                        className={`absolute h-full w-0.5 ${isRTL ? "right-3" : "left-3"} top-0 rounded-full overflow-hidden`}
                                        initial={{ height: 0 }}
                                        animate={{ height: "100%" }}
                                        transition={{ duration: 0.8, delay: 0.4 }}
                                    >
                                        <motion.div
                                            className="absolute inset-0 bg-gradient-to-b from-[#0072CE] via-sky-400 to-[#0072CE]"
                                            animate={{
                                                y: ["-100%", "100%"],
                                            }}
                                            transition={{
                                                repeat: Infinity,
                                                duration: 3,
                                                ease: "linear",
                                            }}
                                        />
                                        <div className="absolute inset-0 bg-gray-100 opacity-60" />
                                    </motion.div>

                                    {personas[activeTab].features &&
                                        Array.isArray(personas[activeTab].features) &&
                                        personas[activeTab].features.map((feature, idx) => (
                                            <motion.li
                                                key={idx}
                                                initial={{ opacity: 0, x: isRTL ? -20 : 20 }}
                                                animate={{ opacity: 1, x: 0 }}
                                                transition={{
                                                    delay: 0.4 + idx * 0.1,
                                                    type: "spring",
                                                    stiffness: 100,
                                                    damping: 10,
                                                }}
                                                className={`flex items-center gap-2.5 sm:gap-3 cursor-pointer group list-none relative ${textAlign}`}
                                            >
                                                <motion.div
                                                    className={`w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-white border-2 border-[#0072CE] flex items-center justify-center text-[#0072CE] z-10 relative shrink-0 ${
                                                        isRTL ? "order-1" : "order-first"
                                                    } overflow-hidden`}
                                                    whileHover={{
                                                        scale: 1.2,
                                                        backgroundColor: "#0072CE",
                                                        color: "white",
                                                        borderColor: "#0072CE",
                                                    }}
                                                    whileTap={{ scale: 0.9 }}
                                                >
                                                    <motion.span initial={{ opacity: 1 }} whileHover={{ opacity: 1 }}>
                                                        <FaCheck className="text-[10px] sm:text-xs" />
                                                    </motion.span>
                                                </motion.div>

                                                <motion.div
                                                    className={`bg-white/80 backdrop-blur-sm px-3 py-2 sm:px-4 sm:py-2.5 rounded-lg shadow-sm flex-1 border border-white/50 group-hover:border-[#0072CE]/30 transition-all duration-300 ${
                                                        isRTL ? "order-2" : "order-last"
                                                    } ${textAlign} relative overflow-hidden`}
                                                >
                                                    <span className="text-gray-800 text-xs sm:text-sm md:text-base font-medium relative z-10">{feature}</span>
                                                </motion.div>
                                            </motion.li>
                                        ))}
                                </motion.ul>

                                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.7 }} className={`relative pt-2 sm:pt-4 ${textAlign}`}>
                                    <motion.a
                                        href="https://api.whatsapp.com/message/GNNOKQBPQVSQB1?autoload=1&app_absent=0"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-block w-full sm:w-auto"
                                        whileHover={{ scale: 1.02 }}
                                        whileTap={{ scale: 0.98 }}
                                    >
                                        <motion.div
                                            className={`relative w-full sm:w-auto px-6 sm:px-8 py-3.5 sm:py-4 rounded-xl sm:rounded-2xl text-white ${personas[activeTab].cta.color} 
                                                     backdrop-blur-md border border-white/20 shadow-lg sm:shadow-2xl overflow-hidden group cursor-pointer
                                                     bg-gradient-to-br from-white/10 to-transparent flex items-center justify-center`}
                                            whileHover={{
                                                boxShadow: "0 20px 40px rgba(0, 0, 0, 0.15)",
                                                y: -2,
                                            }}
                                            transition={{ duration: 0.3, ease: "easeOut" }}
                                        >
                                            <motion.div
                                                className="absolute inset-0 bg-gradient-to-br from-white/20 via-white/10 to-transparent opacity-0"
                                                whileHover={{ opacity: 1 }}
                                                transition={{ duration: 0.3 }}
                                            />

                                            <motion.div
                                                className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -translate-x-full"
                                                animate={{
                                                    translateX: ["-100%", "100%"],
                                                }}
                                                transition={{
                                                    repeat: Infinity,
                                                    repeatType: "loop",
                                                    duration: 3,
                                                    ease: "easeInOut",
                                                    repeatDelay: 2,
                                                }}
                                            />

                                            <motion.div className="absolute inset-0 overflow-hidden pointer-events-none">
                                                {[...Array(8)].map((_, i) => (
                                                    <motion.div
                                                        key={i}
                                                        className="absolute w-1.5 h-1.5 bg-white/40 rounded-full"
                                                        initial={{
                                                            x: `${20 + Math.random() * 60}%`,
                                                            y: `${20 + Math.random() * 60}%`,
                                                            opacity: 0,
                                                            scale: 0,
                                                        }}
                                                        whileHover={{
                                                            opacity: [0, 1, 0],
                                                            scale: [0, 1.2, 0],
                                                            y: [0, -15, -30],
                                                            x: [0, Math.random() * 20 - 10],
                                                        }}
                                                        transition={{
                                                            duration: 1.5 + Math.random() * 0.5,
                                                            delay: i * 0.1,
                                                            repeat: Infinity,
                                                            repeatDelay: Math.random() * 2,
                                                        }}
                                                    />
                                                ))}
                                            </motion.div>

                                            <motion.div
                                                className={`absolute top-0 ${isRTL ? "right-0" : "left-0"} w-16 h-16 bg-gradient-radial from-white/30 to-transparent rounded-full opacity-0`}
                                                whileHover={{ opacity: 1, scale: 1.5 }}
                                                transition={{ duration: 0.4 }}
                                            />

                                            <motion.div className={`relative z-10 flex items-center justify-center gap-3 flex-row`} whileHover={{ x: isRTL ? 2 : -2 }} transition={{ duration: 0.2 }}>
                                                <motion.span className="font-medium text-lg" whileHover={{ scale: 1.05 }} transition={{ duration: 0.2 }}>
                                                    {personas[activeTab].cta.text}
                                                </motion.span>

                                                <motion.div
                                                    className="flex items-center justify-center w-8 h-8 rounded-full bg-white/20 backdrop-blur-sm"
                                                    whileHover={{
                                                        scale: 1.1,
                                                        rotate: isRTL ? 10 : -10,
                                                        backgroundColor: "rgba(255, 255, 255, 0.3)",
                                                    }}
                                                    animate={{
                                                        x: isRTL ? [0, 3, 0] : [0, -3, 0],
                                                    }}
                                                    transition={{
                                                        x: {
                                                            repeat: Infinity,
                                                            duration: 2,
                                                            repeatType: "reverse",
                                                        },
                                                        scale: { duration: 0.2 },
                                                        rotate: { duration: 0.2 },
                                                        backgroundColor: { duration: 0.2 },
                                                    }}
                                                >
                                                    <FaArrowLeft className={`text-sm ${isRTL ? "rotate-180" : ""}`} />
                                                </motion.div>
                                            </motion.div>

                                            <motion.div
                                                className="absolute inset-0 bg-gradient-to-br from-white/10 to-black/10 opacity-0 rounded-2xl"
                                                whileHover={{ opacity: 1 }}
                                                transition={{ duration: 0.3 }}
                                            />

                                            <motion.div
                                                className="absolute inset-0 rounded-2xl border-2 border-white/30 opacity-0"
                                                whileHover={{
                                                    opacity: 1,
                                                    boxShadow: "0 0 20px rgba(255, 255, 255, 0.3)",
                                                }}
                                                transition={{ duration: 0.3 }}
                                            />
                                        </motion.div>
                                    </motion.a>
                                </motion.div>
                            </div>

                            {/* Column 2 (Desktop Only): Multi-Card Auto-Rotating Testimonial with Pagination Dots */}
                            <div className="hidden lg:flex flex-col items-center justify-center order-2">
                                <div
                                    className="w-full max-w-[360px] xl:max-w-[390px] flex flex-col items-center relative"
                                    onMouseEnter={() => setIsHovered(true)}
                                    onMouseLeave={() => setIsHovered(false)}
                                >
                                    <AnimatePresence mode="wait">
                                        <motion.div
                                            key={`testimonial-${activeTab}-${currentCardIndex}`}
                                            initial={{ opacity: 0, scale: 0.94, y: 12 }}
                                            animate={{ opacity: 1, scale: 1, y: 0 }}
                                            exit={{ opacity: 0, scale: 0.94, y: -12 }}
                                            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                                            whileHover={{ y: -6, transition: { duration: 0.25 } }}
                                            className="w-full bg-white/95 backdrop-blur-md rounded-3xl p-6 sm:p-7 border border-slate-100/90 shadow-[0_10px_30px_-10px_rgba(0,56,168,0.08)] hover:shadow-[0_20px_40px_-12px_rgba(0,56,168,0.14)] relative overflow-hidden flex flex-col items-center text-center transition-shadow duration-300 group min-h-[320px] justify-between"
                                        >
                                            {/* Top Corner: Dot Grid Matrix & Soft Sky Quote Mark */}
                                            <div className={`absolute top-4 ${isRTL ? "right-5" : "left-5"} flex items-start gap-1 pointer-events-none`}>
                                                <div className="grid grid-cols-3 gap-1 opacity-20 pt-1">
                                                    {[...Array(9)].map((_, i) => (
                                                        <div key={i} className="w-1 h-1 rounded-full bg-slate-500" />
                                                    ))}
                                                </div>
                                                <div className="text-sky-200/90 group-hover:text-sky-300 transition-colors duration-300">
                                                    <FaQuoteRight className="w-7 h-7" />
                                                </div>
                                            </div>

                                            {/* Top Center: Circular Icon Avatar Badge */}
                                            <motion.div
                                                className="w-18 h-18 sm:w-20 sm:h-20 rounded-full bg-gradient-to-b from-slate-50 via-slate-100 to-blue-50/60 border border-slate-200/60 shadow-inner flex items-center justify-center text-slate-800 text-2xl sm:text-3xl mb-3 relative z-10"
                                                whileHover={{ scale: 1.08 }}
                                                transition={{ type: "spring", stiffness: 300, damping: 15 }}
                                            >
                                                {personas[activeTab].icon}
                                            </motion.div>

                                            {/* 5 Golden Amber Stars */}
                                            <div className="flex items-center justify-center gap-1 mb-3 text-amber-400">
                                                {[...Array(5)].map((_, i) => (
                                                    <FaStar key={i} className="w-4 h-4 fill-current drop-shadow-xs" />
                                                ))}
                                            </div>

                                            {/* Quote Text */}
                                            <p className="text-slate-700 text-sm sm:text-[15px] leading-relaxed font-normal text-center px-2 mb-3 relative z-10">
                                                {personas[activeTab].testimonials[currentCardIndex]?.text || ""}
                                            </p>

                                            {/* Subtle Center Divider */}
                                            <div className="w-14 h-[2px] rounded-full bg-gradient-to-r from-blue-200/40 via-slate-300 to-rose-200/40 mb-2.5 opacity-80" />

                                            {/* Author & Role */}
                                            <div className="relative z-10">
                                                <h4 className="font-bold text-slate-900 text-sm sm:text-base tracking-tight">
                                                    {personas[activeTab].testimonials[currentCardIndex]?.author || ""}
                                                </h4>
                                                <p className="text-xs text-slate-500 font-medium mt-0.5">
                                                    {personas[activeTab].testimonials[currentCardIndex]?.role || ""}
                                                </p>
                                            </div>

                                            {/* Bottom Opposite Corner: Soft Rose/Peach Quote Mark */}
                                            <div className={`absolute bottom-3.5 ${isRTL ? "left-5" : "right-5"} text-rose-200/80 group-hover:text-rose-300 pointer-events-none transition-colors duration-300`}>
                                                <FaQuoteLeft className="w-7 h-7" />
                                            </div>
                                        </motion.div>
                                    </AnimatePresence>

                                    {/* Pagination Dots (Only dots, no buttons as requested) */}
                                    {personas[activeTab]?.testimonials?.length > 1 && (
                                        <div className="flex items-center justify-center gap-1 mt-3 z-10">
                                            {personas[activeTab].testimonials.map((_, dotIdx) => (
                                                <button
                                                    key={dotIdx}
                                                    type="button"
                                                    onClick={() => setCurrentCardIndex(dotIdx)}
                                                    className="p-3 flex items-center justify-center cursor-pointer touch-manipulation"
                                                    aria-label={`اسلاید ${dotIdx + 1}`}
                                                >
                                                    <span
                                                        className={`h-2 rounded-full transition-all duration-300 block ${
                                                            currentCardIndex === dotIdx
                                                                ? "w-6 bg-[#0072CE] shadow-xs"
                                                                : "w-2 bg-slate-300/80 hover:bg-slate-400"
                                                        }`}
                                                    />
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </motion.div>
                </AnimatePresence>
            </div>
        </section>
    );
};

export default HomeUserPersonas;
